import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const TESTIMONIALS = [
  {
    role: "AB Hunter as a Mentor",
    quote: "As an educational practitioner, no single administrator or educational leader has made a larger impression on my instructional practices than Dr. Antiqua Hunter. Her insight and applied wisdom are aimed to improve and develop effective teaching practices and student academic achievement.",
    name: "Saralice K. Campbell, EdD, ELPA",
  },
  {
    role: "AB Hunter as a Leader",
    quote: "Dr. Hunter's leadership is astounding. She leads by recognizing and including others in decision making. She is what gives leadership its very definition.",
    name: "Dr. Victoria Russell — Educator",
  },
  {
    role: "AB Hunter as a Leader",
    quote: "Dr. Hunter was an amazing principal who cared for the students, community and family. Her focus was always what's best for our students and our community.",
    name: "Elsa Dubon — Administrative Assistant",
  },
  {
    role: "AB Hunter as a Consultant",
    quote: "Dr. Antiqua Hunter is a definite game changer for anyone looking for great results. Her goals, actions, and interventions are data driven and research based. Under her guidance, teachers gained confidence and students improved tremendously.",
    name: "Tania Brown — Educator, Henry County Schools",
  },
  {
    role: "AB Hunter as a Community Leader",
    quote: "Under her leadership, the Pulaski County NAACP branch increased its membership by 50%, organized several successful events and campaigns, and set up strong partnerships with other local organizations.",
    name: "Donnita Patterson-Brown — MO NAACP Veterans Affairs Chair",
  },
  {
    role: "AB Hunter as an Educator",
    quote: "I started with basic knowledge and ended with an abundance of understanding. She is poised in her passion to educate and advocate for educators. The opportunity to be part of her class has been everlasting and memorable.",
    name: "Deirdre Huggins — Drury University, Class of 2014",
  },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const t = TESTIMONIALS[index];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="mx-auto max-w-5xl px-5 py-28 sm:px-8 sm:py-36">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-14 text-center"
      >
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Testimonials</span>
        <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          In their words.
        </h2>
      </motion.div>

      <div className="relative rounded-3xl border border-gold/20 bg-ink-card/60 px-6 py-12 sm:px-14 sm:py-16">
        <Quote className="absolute left-8 top-8 h-10 w-10 text-gold/20" />
        <AnimatePresence mode="wait">
          <motion.figure
            key={index}
            data-testid="testimonial-slide"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{t.role}</span>
            <blockquote className="mt-5 font-display text-lg font-medium leading-relaxed text-foreground/90 sm:text-xl">
              "{t.quote}"
            </blockquote>
            <figcaption className="mt-7 text-sm text-muted-foreground">— {t.name}</figcaption>
          </motion.figure>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`Testimonial ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-gold" : "w-1.5 bg-gold/25 hover:bg-gold/50"}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button
              data-testid="testimonial-prev-button"
              aria-label="Previous testimonial"
              onClick={() => setIndex((index - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="testimonial-next-button"
              aria-label="Next testimonial"
              onClick={() => setIndex((index + 1) % TESTIMONIALS.length)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
